import { PROTOCOL, TAG_PATTERN } from './constants';
import {
  CONDITIONS,
  HEIGHT_METHODS,
  OBSERVATION_CHIPS,
  SOIL_MOISTURE,
  SOIL_TEMP,
  type Condition,
  type HeightMethod,
  type ObservationChip,
  type SoilMoisture,
  type SoilTemp,
} from './enums';
import { requiresDbhGirth } from './math';

/** One measurement as the field app writes it, before sync. */
export interface MeasurementInput {
  condition: Condition;
  heightCm?: number | null;
  heightMethod?: HeightMethod | null;
  /** Girths taken this visit: basal (10 cm) and, for tall trees, DBH (130 cm). */
  girths?: ReadonlyArray<{ heightCm: number; girthCm: number }>;
  soilMoisture?: SoilMoisture | null;
  soilTemp?: SoilTemp | null;
  chips?: readonly ObservationChip[];
  note?: string | null;
  hasPhoto?: boolean;
}

export interface ValidationIssue {
  code: string;
  message: string;
  /** warning = the record may be saved; error = it may not. */
  level: 'error' | 'warning';
}

const includes = <T extends string>(set: readonly T[], v: unknown): v is T => (set as readonly string[]).includes(v as string);

/**
 * The record-level rules every measurement must satisfy. The harness in
 * tools/validate.mjs (T1.11) runs the same function over exported data.
 */
export function validateMeasurement(m: MeasurementInput): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const err = (code: string, message: string) => out.push({ code, message, level: 'error' });
  const warn = (code: string, message: string) => out.push({ code, message, level: 'warning' });

  if (!includes(CONDITIONS, m.condition)) {
    err('condition', `condition must be one of ${CONDITIONS.join(', ')}`);
    return out;
  }

  const girths = m.girths ?? [];
  const hasHeight = m.heightCm != null;

  if (m.condition === 'NF') {
    if (hasHeight || girths.length > 0) err('nf_has_measurements', 'a tree recorded NF cannot carry a height or a girth');
    if (m.soilMoisture || m.soilTemp) warn('nf_has_soil', 'soil check recorded for a tree that was not found');
    return out;
  }

  if (hasHeight) {
    const h = m.heightCm as number;
    if (!Number.isFinite(h) || h < 0) err('height_range', 'height must be a non-negative number of cm');
    else if (h > 3000) warn('height_implausible', `height ${h} cm is implausible for a planted tree`);
    if (h > PROTOCOL.HEIGHT_TAPE_LIMIT_CM && !m.heightMethod) {
      warn('height_method_missing', `heights above ${PROTOCOL.HEIGHT_TAPE_LIMIT_CM} cm should record the method`);
    }
  } else if (m.condition !== 'D') {
    err('height_missing', 'a living tree needs a height');
  }

  if (m.heightMethod != null && !includes(HEIGHT_METHODS, m.heightMethod)) err('height_method', 'unknown height method');

  for (const g of girths) {
    if (!(PROTOCOL.GIRTH_HEIGHTS_CM as readonly number[]).includes(g.heightCm)) {
      err('girth_height', `girth height must be ${PROTOCOL.GIRTH_HEIGHTS_CM.join(' or ')} cm, got ${g.heightCm}`);
    }
    if (!Number.isFinite(g.girthCm) || g.girthCm <= 0) err('girth_range', 'girth must be a positive number of cm');
    if (hasHeight && g.heightCm >= (m.heightCm as number)) {
      err('girth_above_top', `girth at ${g.heightCm} cm on a tree ${m.heightCm} cm tall`);
    }
  }
  const heightsSeen = girths.map((g) => g.heightCm);
  if (new Set(heightsSeen).size !== heightsSeen.length) err('girth_duplicate', 'two girths at the same height');

  if (m.condition !== 'D') {
    if (!heightsSeen.includes(PROTOCOL.GIRTH_BASAL_HEIGHT_CM)) {
      err('girth_basal_missing', `every living tree needs a girth at ${PROTOCOL.GIRTH_BASAL_HEIGHT_CM} cm`);
    }
    if (hasHeight && requiresDbhGirth(m.heightCm as number) && !heightsSeen.includes(PROTOCOL.GIRTH_DBH_HEIGHT_CM)) {
      warn('girth_dbh_missing', `trees taller than ${PROTOCOL.DBH_TRIGGER_HEIGHT_CM} cm should also carry a girth at ${PROTOCOL.GIRTH_DBH_HEIGHT_CM} cm`);
    }
  }

  if (m.soilMoisture != null && !includes(SOIL_MOISTURE, m.soilMoisture)) err('soil_moisture', 'unknown soil moisture');
  if (m.soilTemp != null && !includes(SOIL_TEMP, m.soilTemp)) err('soil_temp', 'unknown soil temperature');
  for (const c of m.chips ?? []) if (!includes(OBSERVATION_CHIPS, c)) err('chip', `unknown observation chip ${String(c)}`);

  if (m.condition !== 'D' && !m.hasPhoto) warn('photo_missing', 'the protocol asks for one photo per tree per survey');

  return out;
}

export function isValidTag(tag: string): boolean {
  return TAG_PATTERN.test(tag);
}
